<script setup lang="ts">
import BaseButton from "~/components/Base/BaseButton.vue";
import { useMainStore, useProductPageStore } from "~/stores/main";
import { useQuickViewStore } from "~/stores/quickViewStore";
import { useCartStore } from "~/stores/cartStore";
import { usePriceFormatter } from "~/utils/priceFormatter";
import {
  quickViewAddToCart as sendQuickViewAddToCartEvent,
  viewItem as sendViewItemEvent,
} from "~/composables/useAnalytics";

// -----------------------
// composables
// -----------------------
const { status, data } = useAuth();
const defaultStore = useProductPageStore();
const quickViewStore = useQuickViewStore();
const mainStore = useMainStore();
const cartStore = useCartStore();
// -----------------------
// props & emits
// -----------------------
const props = defineProps<{
  product: Record<string, any>;
  useQuickViewStoreProp?: boolean;
}>();

// Use quick view store if specified, otherwise use default product page store
const store = props.useQuickViewStoreProp ? quickViewStore : defaultStore;

const emit = defineEmits<{
  close: [];
  addedToCart: [];
}>();
const {
  totalStockOfVariant,
  getAssetUrl,
  isRealContent,
  getVariantByProperty,
} = useHelpers();
const { t } = useI18n();
const config = useRuntimeConfig();
const themeGroup = config.public.theme.group;

// -----------------------
// reactive properties
// -----------------------
const addingToBasketRef = ref<boolean>(false);
const quantityRef = ref<number>(1);
const isAddToCartButtonShaking = ref<boolean>(false);
const isLoading = ref<boolean>(true);

// -----------------------
// computed properties
// -----------------------
const variantRef = computed(() => {
  try {
    if (
      store.size === null ||
      store.color === null ||
      !store.masterProduct?.products
    ) {
      return null;
    }

    return store.masterProduct.products.find((variant: Record<any, any>) => {
      return (
        variant.product.product_meret_property === store.size &&
        variant.product.product_szin_property === store.color
      );
    });
  } catch (error) {
    // console.error("Error computing variant:", error);
    return null;
  }
});

const maxQuantityRef = computed(() => {
  if (variantRef.value) {
    return totalStockOfVariant(variantRef.value);
  }

  return 0;
});

const priceRef = computed(() => {
  try {
    if (variantRef.value) {
      if (status.value === "authenticated") {
        if (
          Array.isArray(data?.value?.usergroups) &&
          data?.value?.usergroups?.includes(1) &&
          Array.isArray(variantRef.value.product.prices) &&
          variantRef.value.product.prices.length > 0
        ) {
          return usePriceFormatter(
            {
              original_price: parseInt(variantRef.value.product.original_price),
              price: parseInt(variantRef.value.product.price),
              pricelist_original_price: parseFloat(
                variantRef.value.product.prices[0].original_price,
              ),
              pricelist_price: parseFloat(
                variantRef.value.product.prices[0].price,
              ),
            },
            "product",
          );
        }
      }

      return usePriceFormatter(
        {
          original_price: parseFloat(variantRef.value.product.original_price),
          price: parseFloat(variantRef.value.product.price),
        },
        "product",
      );
    }

    return store.masterProduct
      ? usePriceFormatter(store.masterProduct, "masterProduct")
      : "";
  } catch (error) {
    // console.error("Error computing price:", error);
    return "";
  }
});

const manufacturerLogo = computed(() => {
  const folder = config.public.routes.manufacturers;

  return store.manufacturer?.logo
    ? getAssetUrl(`Public/assets/webshop/${folder}/${store.manufacturer?.logo}`)
    : "";
});

const isAddToCartDisabled = computed(() => {
  return (
    !store.color ||
    !store.size ||
    maxQuantityRef.value === 0 ||
    addingToBasketRef.value ||
    !store.masterProduct
  );
});

const addToCartButtonText = computed(() => {
  if (isAddToCartButtonShaking.value) {
    if (!store.size) {
      return t("product_page.select_size");
    } else if (maxQuantityRef.value === 0) {
      return t("out_of_stock");
    } else {
      return t("add_to_cart");
    }
  } else {
    return t("add_to_cart");
  }
});

const hasDescription = computed(() => {
  const description = store.masterProduct?.description;

  return (
    description && typeof description === "string" && isRealContent(description)
  );
});

const images = computed(() => {
  try {
    // Use store.images directly, but only if it has content
    // If store.images is empty but we have a masterProduct, use fallback logic
    if (
      store.images &&
      Array.isArray(store.images) &&
      store.images.length > 0
    ) {
      return store.images;
    }

    // Fallback to default logic if store.images is empty or not set
    if (!store.masterProduct) {
      return [];
    }

    const products = store.masterProduct.products;
    if (!products || !Array.isArray(products)) {
      return [];
    }

    const productsInStock = products.filter(
      (variant) => totalStockOfVariant(variant) > 0,
    );
    let defaultColor;

    if (products.length === 1) {
      defaultColor = products[0].product.product_szin_property;
    } else if (productsInStock.length > 0) {
      defaultColor = productsInStock[0].product.product_szin_property;
    } else {
      defaultColor = products[0].product.product_szin_property;
    }

    if (defaultColor !== null) {
      store.color = defaultColor;
      const variantWithColor = getVariantByProperty(
        store.masterProduct,
        "product_szin_property",
        defaultColor,
      );

      if (variantWithColor && variantWithColor.images) {
        return variantWithColor.images.map((src) => getAssetUrl(src));
      }
    }

    return [];
  } catch (error) {
    // console.error("Error computing images:", error);
    return [];
  }
});

// -----------------------
// helper functions
// -----------------------
const addToBasket = async () => {
  try {
    addingToBasketRef.value = true;
    if (store.selectedVariant) {
      const added = await cartStore.addProduct(
        store.selectedVariant,
        quantityRef.value,
      );
      if (added) {
        sendQuickViewAddToCartEvent(
          store.masterProduct,
          variantRef.value,
          quantityRef.value,
        );
        addingToBasketRef.value = false;
        await cartStore.fetchCartData(true);
        mainStore.cartVisible = true;
        emit("addedToCart");
      }
    }
  } catch (error) {
    // console.error("Error adding to basket:", error);
  } finally {
    addingToBasketRef.value = false;
  }
};

const urgeUserToSelect = () => {
  isAddToCartButtonShaking.value = true;

  setTimeout(() => {
    isAddToCartButtonShaking.value = false;
  }, 1000);
};

// -----------------------
// vue events
// -----------------------
onMounted(async () => {
  try {
    // Initialize the store with the product data
    store.masterProduct = props.product;

    // Reset image counter to first image
    store.currentImageIndex = 0;

    // Auto-select first available color if no color is selected
    if (!store.color && store.masterProduct) {
      const products = store.masterProduct.products;
      if (products && Array.isArray(products)) {
        const productsInStock = products.filter(
          (variant) => totalStockOfVariant(variant) > 0,
        );

        let defaultColor;
        if (products.length === 1) {
          defaultColor = products[0].product.product_szin_property;
        } else if (productsInStock.length > 0) {
          defaultColor = productsInStock[0].product.product_szin_property;
        } else {
          defaultColor = products[0].product.product_szin_property;
        }

        if (defaultColor) {
          store.color = defaultColor;
          const variantWithColor = getVariantByProperty(
            store.masterProduct,
            "product_szin_property",
            defaultColor,
          );

          if (variantWithColor && variantWithColor.images) {
            store.images = variantWithColor.images.map((src) =>
              getAssetUrl(src),
            );
          }
        }
      }
    } else if (store.color && store.masterProduct) {
      // Use existing color if already set
      const variantWithColor = getVariantByProperty(
        store.masterProduct,
        "product_szin_property",
        store.color,
      );

      if (variantWithColor) {
        store.images = variantWithColor.images.map((src) => getAssetUrl(src));
      }
    }

    if (variantRef.value) {
      store.selectedVariant = variantRef.value.product.id;
      store.selectedVariantSku = variantRef.value.product.sku;
    }

    // Fetch manufacturer data for logo
    if (store.masterProduct?.masterproduct_gyarto_property) {
      try {
        const manufacturers = await $fetch(`/api/meilisearch/manufacturers`, {
          method: "POST",
          body: {
            name: store.masterProduct.masterproduct_gyarto_property,
          },
        });

        if (Array.isArray(manufacturers) && manufacturers.length > 0) {
          store.manufacturer = manufacturers[0];
        }
      } catch (error) {
        // console.error("Failed to fetch manufacturer data:", error);
      }
    }
  } catch (error) {
    // console.error("Error initializing quick view:", error);
  } finally {
    // Set loading to false after everything is initialized
    isLoading.value = false;
  }
});

onUnmounted(() => {
  try {
    // Reset the store to clean state
    store.masterProduct = null;
    store.manufacturer = null;
    store.color = null;
    store.size = null;
    store.selectedVariant = null;
    store.selectedVariantSku = null;
    store.images = [];
    store.currentImageIndex = 0;
  } catch (error) {
    // console.error("Error restoring store:", error);
  }
});
</script>

<template>
  <div class="quick-view-product-details" @click.stop>
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center h-64">
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-search mx-auto mb-4"
        ></div>
        <p class="text-gray-600">{{ t("loading") }}</p>
      </div>
    </div>

    <!-- Product Content -->
    <div v-else class="grid grid-cols-5 gap-6" @click.stop>
      <!-- Row 1: Product Images (Column 1-2) -->
      <div class="col-span-2" @click.stop>
        <div v-if="!isLoading" class="product-images">
          <ProductPageGalleryMobile
            :images="images"
            :show-on-desktop="true"
            :store="store"
          />
        </div>
      </div>

      <!-- Row 1: Product Info (Columns 2-5) -->
      <div class="col-span-3 product-info">
        <!-- Manufacturer & product name -->
        <div
          v-if="store.masterProduct && !isLoading"
          class="flex justify-between items-center mb-3"
        >
          <div v-if="themeGroup === 'mountex'" class="text-brand-headline">
            <h1 class="!text-xl lg:!text-2xl !mt-[0] font-bold mb-1">
              {{ store.masterProduct?.masterproduct_gyarto_property }}
            </h1>
            <h2 class="!text-sm lg:!text-xl !mt-0">
              {{ store.masterProduct?.name }}
            </h2>
          </div>
          <h1 v-else class="font-bold !text-xl lg:!text-2xl !mt-[0]">
            {{ store.masterProduct?.name }}
          </h1>

          <div v-if="store.manufacturer && manufacturerLogo" class="ml-4">
            <NuxtImg
              v-if="config.public.theme.group === 'tnf'"
              :alt="store.manufacturer.name"
              :src="manufacturerLogo"
              loading="lazy"
              width="80"
            />
            <a
              v-else
              :href="`/${config.public.routes.manufacturers}/${store.manufacturer.slug}`"
            >
              <NuxtImg
                class="cursor-pointer"
                :alt="store.manufacturer.name"
                :src="manufacturerLogo"
                loading="lazy"
                width="80"
              />
            </a>
          </div>
        </div>

        <!-- SKU -->
        <div
          v-if="store.masterProduct && !isLoading && store.masterProduct?.stock"
          class="text-xs mb-2"
        >
          {{ t("sku") }}:
          {{ store.selectedVariantSku || t("product_page.select_size") }}
        </div>

        <!-- Price(s) -->
        <div
          v-if="
            store.masterProduct &&
            !isLoading &&
            store.masterProduct?.stock &&
            priceRef
          "
          class="text-lg lg:text-xl leading-6 mb-4"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="priceRef" />
          <!-- eslint-enable -->
        </div>

        <!-- Variants Selection -->
        <ProductPageVariants
          v-if="store.masterProduct && !isLoading"
          :size-guide="false"
          :disable-url-updates="true"
          :store="store"
          @select="sendViewItemEvent"
        />

        <!-- Cart button -->
        <div
          v-if="
            store.masterProduct &&
            !isLoading &&
            (store.masterProduct?.stock ?? 0 > 0)
          "
          class="add-to-cart-container relative flex justify-center h-[45px] lg:h-[50px] mt-4"
        >
          <BaseButton
            type="primary"
            class="add-to-cart text-lg px-[40px] bg-brand-search min-h-[45px] lg:min-h-[50px]"
            :class="{ shaking: isAddToCartButtonShaking }"
            :disabled="isAddToCartDisabled"
            @click="addToBasket()"
            >{{ addToCartButtonText }}</BaseButton
          >
          <div
            v-if="isAddToCartDisabled"
            class="absolute w-full h-full cursor-not-allowed z-20 top-0"
            @click.capture="urgeUserToSelect"
          ></div>
        </div>
        <div v-else-if="store.masterProduct && !isLoading">
          <BaseButton type="green" class="text-lg mt-4" :disabled="true">{{
            t("non_orderable")
          }}</BaseButton>
        </div>
      </div>

      <!-- Row 2: Stock Info and Description (All 5 columns) -->
      <div v-if="false" class="col-span-5">
        <!-- Stock Info -->
        <div
          v-if="
            store.masterProduct &&
            !isLoading &&
            (store.masterProduct?.stock ?? 0 > 0)
          "
          class="mb-4"
        >
          <ProductPageStockInfoROSK
            v-if="
              ['mountexro', 'mountexsk'].includes(
                useRuntimeConfig().public.theme.site,
              )
            "
          />
          <ProductPageStockInfo v-else />
        </div>
        <div
          v-else-if="store.masterProduct && !isLoading"
          class="mb-4 text-brand-highlight"
        >
          <span class="m-icon-cancel mr-3" />{{ t("out_of_stock") }}
        </div>

        <!-- Description (compact) -->
        <div>
          <ProductPageDescription
            v-if="store.masterProduct && !isLoading && hasDescription"
            :is-in-modal="true"
          />
          <ProductPageAttributes />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.quick-view-product-details {
  max-width: 1200px;
  margin: 0 auto;
}

.product-images {
  max-height: 400px;
  overflow: hidden;
}

.product-info {
  padding: 0 1rem;
}

@media (max-width: 768px) {
  .quick-view-product-details {
    max-width: 100%;
  }

  .product-images {
    max-height: 250px;
  }

  .product-info {
    padding: 0 0.5rem;
  }
}

@keyframes horizontal-shaking {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(7px);
  }
  50% {
    transform: translateX(-7px);
  }
  75% {
    transform: translateX(7px);
  }
  100% {
    transform: translateX(0);
  }
}

.add-to-cart.shaking {
  animation-name: horizontal-shaking;
  animation-duration: 400ms;
  animation-delay: 200ms;
  animation-iteration-count: 2;
}

.add-to-cart-container,
.add-to-cart:not(:disabled) {
  transition-property: width, height;
  transition-duration: 500ms;
}

.add-to-cart {
  width: 100%;
}

@media (max-width: 768px) {
  .product-info {
    padding: 0 0.5rem;
  }
}
</style>
